import { ReactNode } from 'react';

import {
    DEFAULT_SITELINKS_COUNT,
    SITELINK_DESCR_LENGTH,
    SITELINK_TITLE_LENGTH,
    SUMMARY_SITELINKS_TITLE_LENGTH
} from '../../constants';
import { BannerConstants, SiteLinkData } from '../../typings/banners';
import {
    BannerWithSitelinks,
    EditBannersState,
    EditedBanner,
    getEditedBannerWithSitelinks
} from '../../typings/bannersEditState';
import { ValidationResultItem } from '../../typings/common';
import { bannersTextErrorMapper } from '../BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { getCollapsedSiteLinks } from '../BannerHelpers/BannerHelpers';
import { UrlHelpers } from '../UrlHelpers';

import { noInvalidSymbols } from './helpers';

type ValidationRule = {
    code: string;
    isValid: (data: ValidationData | ValidationSiteLinkData) => boolean;
    getParams?: (constants: BannerConstants) => Record<string, {}>;
};

type ValidationData = {
    banner: EditedBanner;
    state: EditBannersState;
};

type ValidationSiteLinkData = {
    sitelink: SiteLinkData;
    banner: BannerWithSitelinks;
    state: EditBannersState;
};

export class SitelinksValidator {
    static rules = {
        common: [
            {
                code: 'SitelinkDefectIds.Gen.DUPLICATE_SITELINK_TITLES',
                isValid: function(data: ValidationData): boolean {
                    const titles: string[] = [];
                    const banner = getEditedBannerWithSitelinks(data.banner);
                    if (!banner) {
                        return true;
                    }

                    return banner.sitelinks.items.every(link => {
                        if (!link.title.length) {
                            return true;
                        }
                        if (titles.includes(link.title)) {
                            return false;
                        } else {
                            titles.push(link.title);
                            return true;
                        }
                    });
                }
            },
            {
                code: 'SitelinkDefectIds.Gen.DUPLICATE_SITELINK_DESCS',
                isValid: function(data: ValidationData): boolean {
                    const descs: string[] = [];
                    const banner = getEditedBannerWithSitelinks(data.banner);
                    if (!banner) {
                        return true;
                    }

                    return banner.sitelinks.items.every(link => {
                        if (!link.descr.length) {
                            return true;
                        }
                        if (descs.includes(link.descr)) {
                            return false;
                        } else {
                            descs.push(link.descr);
                            return true;
                        }
                    });
                }
            },
            {
                code: 'SitelinkDefectIds.Strings.MAX_SITELINKS_TITLES_LENGTH',
                isValid: function(data: ValidationData): boolean {
                    const banner = getEditedBannerWithSitelinks(data.banner);
                    if (!banner) {
                        return true;
                    }

                    const collapsedSiteLinks = getCollapsedSiteLinks(
                        banner.sitelinks.items
                    );

                    if (collapsedSiteLinks.length > DEFAULT_SITELINKS_COUNT) {
                        return true;
                    }

                    return (
                        collapsedSiteLinks.map(link => link.title).join('')
                            .length <= SUMMARY_SITELINKS_TITLE_LENGTH
                    );
                },
                getParams: function(
                    constants: BannerConstants
                ): Record<string, {}> {
                    return {
                        maxLength: SUMMARY_SITELINKS_TITLE_LENGTH
                    };
                }
            },
            {
                code:
                    'SitelinkDefectIds.Strings.MAX_SITELINKS_FIRST_BLOCK_TITLES_LENGTH',
                isValid: function(data: ValidationData): boolean {
                    const banner = getEditedBannerWithSitelinks(data.banner);
                    if (!banner) {
                        return true;
                    }

                    const collapsedSiteLinks = getCollapsedSiteLinks(
                        banner.sitelinks.items
                    );

                    if (collapsedSiteLinks.length <= DEFAULT_SITELINKS_COUNT) {
                        return true;
                    }

                    const siteLinksFirstBLock = collapsedSiteLinks.slice(
                        0,
                        DEFAULT_SITELINKS_COUNT
                    );

                    return (
                        siteLinksFirstBLock.map(link => link.title).join('')
                            .length <= SUMMARY_SITELINKS_TITLE_LENGTH
                    );
                },
                getParams: function(
                    constants: BannerConstants
                ): Record<string, {}> {
                    return {
                        maxLength: SUMMARY_SITELINKS_TITLE_LENGTH
                    };
                }
            },
            {
                code:
                    'SitelinkDefectIds.Strings.MAX_SITELINKS_SECOND_BLOCK_TITLES_LENGTH',
                isValid: function(data: ValidationData): boolean {
                    const banner = getEditedBannerWithSitelinks(data.banner);
                    if (!banner) {
                        return true;
                    }

                    const collapsedSiteLinks = getCollapsedSiteLinks(
                        banner.sitelinks.items
                    );

                    if (collapsedSiteLinks.length <= DEFAULT_SITELINKS_COUNT) {
                        return true;
                    }

                    const siteLinksSecondBLock = collapsedSiteLinks.slice(
                        DEFAULT_SITELINKS_COUNT
                    );

                    return (
                        siteLinksSecondBLock.map(link => link.title).join('')
                            .length <= SUMMARY_SITELINKS_TITLE_LENGTH
                    );
                },
                getParams: function(
                    constants: BannerConstants
                ): Record<string, {}> {
                    return {
                        maxLength: SUMMARY_SITELINKS_TITLE_LENGTH
                    };
                }
            }
        ],
        title: [
            {
                code:
                    'SitelinkDefectIds.Strings.SITELINK_TITLE_LENGTH_CANNOT_BE_MORE_THAN_MAX',
                isValid: function(data: ValidationSiteLinkData): boolean {
                    return data.sitelink.title.length <= SITELINK_TITLE_LENGTH;
                },
                getParams: function(
                    constants: BannerConstants
                ): Record<string, {}> {
                    return {
                        maxLength: SITELINK_TITLE_LENGTH
                    };
                }
            },
            {
                code:
                    'SitelinkDefectIds.Strings.ALLOWED_SYMBOLS_SITELINK_TITLE',
                isValid: function(data: ValidationSiteLinkData): boolean {
                    return noInvalidSymbols(
                        data.sitelink.title,
                        data.state.constants.sitelinks.allowSitelinkLetters
                    );
                }
            }
        ],
        link: [
            {
                code: 'SitelinkDefectIds.Gen.INVALID_SITELINK_HREF',
                isValid: function(data: ValidationSiteLinkData): boolean {
                    return (
                        (data.sitelink.title.length === 0 ||
                            UrlHelpers.isUrl(data.sitelink.link) ||
                            Boolean(data.sitelink.turbo)) &&
                        data.sitelink.isUrlValid
                    );
                }
            }
        ],
        descr: [
            {
                code:
                    'SitelinkDefectIds.Strings.SITELINK_DESCRIPTION_LENGTH_CANNOT_BE_MORE_THAN_MAX',
                isValid: function(data: ValidationSiteLinkData): boolean {
                    return data.sitelink.descr.length <= SITELINK_DESCR_LENGTH;
                },
                getParams: function(
                    constants: BannerConstants
                ): Record<string, {}> {
                    return {
                        maxLength: SITELINK_DESCR_LENGTH
                    };
                }
            }
        ]
    };

    static mapTextToErrors(
        errors: ValidationRule[],
        path: string,
        banner: EditedBanner,
        constants: BannerConstants
    ): Array<{ code: string; text: ReactNode }> {
        return errors.map((validationRule: ValidationRule) => {
            return {
                code: validationRule.code,
                text: bannersTextErrorMapper({
                    code: validationRule.code,
                    path: path,
                    params: validationRule.getParams
                        ? validationRule.getParams(constants)
                        : null
                })
            };
        });
    }

    static validateSitelink(
        sitelink: SiteLinkData,
        path: string,
        banner: BannerWithSitelinks,
        state: EditBannersState
    ): ValidationResultItem[] {
        return Object.keys(sitelink).reduce(
            (result: ValidationResultItem[], field: string) => {
                const invalidRules = (
                    SitelinksValidator.rules[field] || []
                ).filter(
                    (validationRule: ValidationRule) =>
                        !validationRule.isValid({ sitelink, banner, state })
                );
                if (invalidRules.length) {
                    result = [
                        ...result,
                        ...invalidRules.map(
                            (validationRule: ValidationRule) => {
                                return {
                                    code: validationRule.code,
                                    path: `${path}.${field}`,
                                    params: validationRule.getParams
                                        ? validationRule.getParams(
                                              state.constants
                                          )
                                        : null
                                };
                            }
                        )
                    ];
                }

                return result;
            },
            []
        );
    }

    static validateSitelinks(
        path: string,
        tmpBanner: EditedBanner,
        state: EditBannersState
    ): ValidationResultItem[] {
        const banner = getEditedBannerWithSitelinks(tmpBanner);
        if (!banner) {
            return [];
        }
        const sitelinks = banner.sitelinks.items;

        return sitelinks.reduce(
            (result: ValidationResultItem[], sitelink, index) => {
                const sitelinkErrors = SitelinksValidator.validateSitelink(
                    sitelink,
                    `${path}[${index}]`,
                    banner,
                    state
                );

                if (sitelinkErrors.length > 0) {
                    result = [...result, ...sitelinkErrors];
                }

                return result;
            },
            []
        );
    }

    static validate(
        path: string,
        banner: EditedBanner,
        state: EditBannersState
    ): ValidationResultItem[] {
        let result: ValidationResultItem[] = [];
        const commonErrors = (SitelinksValidator.rules.common || []).filter(
            (validationRule: ValidationRule) =>
                !validationRule.isValid({ banner, state })
        );
        const sitelinksErrors = SitelinksValidator.validateSitelinks(
            path,
            banner,
            state
        );

        if (commonErrors.length) {
            result = commonErrors.map((validationRule: ValidationRule) => {
                return {
                    code: validationRule.code,
                    path: path,
                    params: validationRule.getParams
                        ? validationRule.getParams(state.constants)
                        : null
                };
            });
        }

        if (Object.keys(sitelinksErrors).length) {
            result = [...result, ...sitelinksErrors];
        }

        return result;
    }
}
